/**
 * Cash Flow Projections Panel (SPRINT-6B F011)
 *
 * Deal-level cash flow modelling with multiple scenarios.
 * Features:
 * - Empty state with "Create Projection" button
 * - Projection cards showing name, scenario badge, date range, totals
 * - Create/Edit modal with form fields
 * - Delete confirmation AlertDialog
 * - Summary: Total Inflows, Outflows, Net Cash Flow
 * - Debt Service coverage badge (Pass/Fail)
 *
 * Test IDs:
 * - cash-flow-projections-panel
 * - debt-service-badge
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { TrendingUp, Plus, Edit, Trash2, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';

// Types
interface CashFlowProjection {
  id: string;
  dealId: string;
  projectionName: string;
  projectionType: string;
  scenarioType: string;
  startDate: string;
  endDate: string;
  periods: number;
  projectionData: any;
  assumptions: any;
  totalInflows: number;
  totalOutflows: number;
  netCashFlow: number;
  coversDebtService: boolean;
  debtServiceBuffer: number;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CashFlowProjectionsPanelProps {
  dealId: string;
}

// Scenario badge colours
const SCENARIO_VARIANTS: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  base_case: 'default',
  best_case: 'secondary',
  worst_case: 'outline',
  stress_test: 'destructive',
};

const SCENARIO_LABELS: Record<string, string> = {
  base_case: 'Base Case',
  best_case: 'Best Case',
  worst_case: 'Worst Case',
  stress_test: 'Stress Test',
};

const PROJECTION_TYPE_LABELS: Record<string, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annual: 'Annual',
};

// Format currency
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

// Form state
interface ProjectionFormData {
  projectionName: string;
  projectionType: string;
  scenarioType: string;
  startDate: string;
  endDate: string;
  notes: string;
}

const EMPTY_FORM: ProjectionFormData = {
  projectionName: '',
  projectionType: 'monthly',
  scenarioType: 'base_case',
  startDate: '',
  endDate: '',
  notes: '',
};

export const CashFlowProjectionsPanel: React.FC<CashFlowProjectionsPanelProps> = ({ dealId }) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingProjection, setEditingProjection] = useState<CashFlowProjection | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CashFlowProjection | null>(null);
  const [formData, setFormData] = useState<ProjectionFormData>(EMPTY_FORM);

  // Fetch projections
  const { data: projections = [], isLoading } = useQuery<CashFlowProjection[]>({
    queryKey: [`/api/crm/deals/${dealId}/cash-flow-projections`],
    enabled: !!dealId,
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: ProjectionFormData) => {
      const res = await apiRequest('POST', `/api/crm/deals/${dealId}/cash-flow-projections`, {
        ...data,
        projectionData: { periods: [] },
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/deals/${dealId}/cash-flow-projections`] });
      toast({ title: 'Projection created', description: 'Cash flow projection created successfully' });
      closeModal();
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to create projection', variant: 'destructive' });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<ProjectionFormData> }) => {
      const res = await apiRequest('PATCH', `/api/crm/deals/${dealId}/cash-flow-projections/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/deals/${dealId}/cash-flow-projections`] });
      toast({ title: 'Projection updated', description: 'Cash flow projection updated successfully' });
      closeModal();
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to update projection', variant: 'destructive' });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest('DELETE', `/api/crm/deals/${dealId}/cash-flow-projections/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/deals/${dealId}/cash-flow-projections`] });
      toast({ title: 'Projection deleted', description: 'Cash flow projection removed' });
      setDeleteTarget(null);
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to delete projection', variant: 'destructive' });
    },
  });

  function openCreateModal() {
    setEditingProjection(null);
    setFormData(EMPTY_FORM);
    setModalOpen(true);
  }

  function openEditModal(projection: CashFlowProjection) {
    setEditingProjection(projection);
    setFormData({
      projectionName: projection.projectionName,
      projectionType: projection.projectionType,
      scenarioType: projection.scenarioType,
      startDate: projection.startDate,
      endDate: projection.endDate,
      notes: projection.notes || '',
    });
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditingProjection(null);
    setFormData(EMPTY_FORM);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (editingProjection) {
      updateMutation.mutate({ id: editingProjection.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  // Loading state
  if (isLoading) {
    return (
      <Card data-testid="cash-flow-projections-panel">
        <CardHeader className="pb-3">
          <Skeleton className="h-5 w-56" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card data-testid="cash-flow-projections-panel">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Cash Flow Projections
            </CardTitle>
            {projections.length > 0 && (
              <Button variant="outline" size="sm" onClick={openCreateModal}>
                <Plus className="h-4 w-4 mr-1" />
                Create Projection
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {projections.length === 0 ? (
            // Empty state
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <TrendingUp className="h-10 w-10 text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground mb-2">No cash flow projections</p>
              <p className="text-xs text-muted-foreground mb-4">
                Create projections to model debt servicing capacity
              </p>
              <Button variant="outline" size="sm" onClick={openCreateModal}>
                <Plus className="h-4 w-4 mr-1" />
                Create Projection
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {projections.map((projection) => (
                <div
                  key={projection.id}
                  className="border rounded-lg p-4 space-y-3"
                >
                  {/* Header row: name + scenario badge + actions */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{projection.projectionName}</span>
                      <Badge variant={SCENARIO_VARIANTS[projection.scenarioType] || 'default'}>
                        {SCENARIO_LABELS[projection.scenarioType] || projection.scenarioType}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {PROJECTION_TYPE_LABELS[projection.projectionType] || projection.projectionType}
                      </Badge>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditModal(projection)}
                        aria-label="edit"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteTarget(projection)}
                        aria-label="delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Date range */}
                  <p className="text-xs text-muted-foreground">
                    {projection.startDate} to {projection.endDate}
                  </p>

                  {/* Summary: inflows, outflows, net */}
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground">Total Inflows</p>
                      <p className="font-medium text-green-600">{formatCurrency(projection.totalInflows)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Total Outflows</p>
                      <p className="font-medium text-red-600">{formatCurrency(projection.totalOutflows)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Net Cash Flow</p>
                      <p className="font-medium">{formatCurrency(projection.netCashFlow)}</p>
                    </div>
                  </div>

                  {/* Debt service badge */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Debt Service:</span>
                    <Badge
                      data-testid="debt-service-badge"
                      variant={projection.coversDebtService ? 'default' : 'destructive'}
                    >
                      {projection.coversDebtService ? 'Pass' : 'Fail'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={(open) => { if (!open) closeModal(); }}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              {editingProjection ? 'Edit Cash Flow Projection' : 'Create Cash Flow Projection'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              {/* Projection Name */}
              <div className="grid gap-2">
                <Label htmlFor="projectionName">Projection Name</Label>
                <Input
                  id="projectionName"
                  name="projectionName"
                  data-testid="projection-name-input"
                  value={formData.projectionName}
                  onChange={(e) => setFormData({ ...formData, projectionName: e.target.value })}
                  placeholder="e.g. FY2025 Base Case"
                  required
                />
              </div>

              {/* Scenario Type */}
              <div className="grid gap-2">
                <Label htmlFor="scenarioType">Scenario Type</Label>
                <select
                  id="scenarioType"
                  name="scenarioType"
                  data-testid="scenario-type-select"
                  value={formData.scenarioType}
                  onChange={(e) => setFormData({ ...formData, scenarioType: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  required
                >
                  <option value="base_case">Base Case</option>
                  <option value="best_case">Best Case</option>
                  <option value="worst_case">Worst Case</option>
                  <option value="stress_test">Stress Test</option>
                </select>
              </div>

              {/* Projection Type */}
              <div className="grid gap-2">
                <Label htmlFor="projectionType">Projection Type</Label>
                <select
                  id="projectionType"
                  name="projectionType"
                  data-testid="projection-type-select"
                  value={formData.projectionType}
                  onChange={(e) => setFormData({ ...formData, projectionType: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  required
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annual">Annual</option>
                </select>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="startDate">Start Date</Label>
                  <Input
                    id="startDate"
                    name="startDate"
                    data-testid="start-date-input"
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    required
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="endDate">End Date</Label>
                  <Input
                    id="endDate"
                    name="endDate"
                    data-testid="end-date-input"
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    required
                  />
                </div>
              </div>

              {/* Notes */}
              <div className="grid gap-2">
                <Label htmlFor="notes">Notes</Label>
                <Input
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Optional notes"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeModal}>Cancel</Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                {editingProjection ? 'Save Changes' : 'Create Projection'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation AlertDialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Projection</AlertDialogTitle>
            <AlertDialogDescription>
              Remove &quot;{deleteTarget?.projectionName}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-1" />
              )}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default CashFlowProjectionsPanel;
